import { supabase } from '../lib/supabase';
import type {
  Form,
  FormField,
  FormAnswer,
  FormWithFields,
  FormSubmissionWithAnswers,
  CreateFormInput,
  UpdateFormInput,
  CreateFormFieldInput,
  UpdateFormFieldInput,
  LeadTypeDefault,
} from '../types/forms';
import { createTodoForNewLead } from './todoService';
import { logger } from '../lib/logger';

export async function getForms(userId: string): Promise<Form[]> {
  const { data, error } = await supabase
    .from('forms')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function getFormById(formId: string, userId: string): Promise<FormWithFields | null> {
  const { data: form, error } = await supabase
    .from('forms')
    .select('*')
    .eq('id', formId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  if (!form) return null;

  const { data: fields, error: fieldsError } = await supabase
    .from('form_fields')
    .select('*')
    .eq('form_id', formId)
    .order('field_order', { ascending: true });

  if (fieldsError) throw fieldsError;

  return { ...form, fields: fields || [] };
}

export async function getPublicFormBySlug(slug: string): Promise<FormWithFields | null> {
  const { data: form, error } = await supabase
    .from('forms')
    .select('*')
    .eq('slug', slug)
    .eq('is_published', true)
    .maybeSingle();

  if (error) throw error;
  if (!form) return null;

  const { data: fields, error: fieldsError } = await supabase
    .from('form_fields')
    .select('*')
    .eq('form_id', form.id)
    .order('field_order', { ascending: true });

  if (fieldsError) throw fieldsError;

  return { ...form, fields: fields || [] };
}

export async function createForm(userId: string, input: CreateFormInput): Promise<Form> {
  const { data, error } = await supabase
    .from('forms')
    .insert({
      ...input,
      user_id: userId,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateForm(
  formId: string,
  userId: string,
  input: UpdateFormInput
): Promise<Form> {
  const { data, error } = await supabase
    .from('forms')
    .update({
      ...input,
      updated_at: new Date().toISOString(),
    })
    .eq('id', formId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteForm(formId: string, userId: string): Promise<void> {
  // Check if this is a system form
  const { data: form } = await supabase
    .from('forms')
    .select('is_system')
    .eq('id', formId)
    .eq('user_id', userId)
    .single();

  if (form?.is_system) {
    throw new Error('System-Formulare können nicht gelöscht werden');
  }

  const { error } = await supabase
    .from('forms')
    .delete()
    .eq('id', formId)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function createFormField(
  userId: string,
  input: CreateFormFieldInput
): Promise<FormField> {
  const { data, error } = await supabase
    .from('form_fields')
    .insert({
      ...input,
      user_id: userId,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateFormField(
  fieldId: string,
  userId: string,
  input: UpdateFormFieldInput
): Promise<FormField> {
  const { data, error } = await supabase
    .from('form_fields')
    .update(input)
    .eq('id', fieldId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteFormField(fieldId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('form_fields')
    .delete()
    .eq('id', fieldId)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function updateFieldOrders(
  userId: string,
  updates: { id: string; field_order: number }[]
): Promise<void> {
  for (const update of updates) {
    await supabase
      .from('form_fields')
      .update({ field_order: update.field_order })
      .eq('id', update.id)
      .eq('user_id', userId);
  }
}

interface SubmissionData {
  [fieldKey: string]: string;
}

export async function submitPublicForm(
  form: FormWithFields,
  data: SubmissionData
): Promise<{ success: boolean; error?: string }> {
  try {
    let email: string | null = null;
    let fullName: string | null = null;

    for (const field of form.fields) {
      const value = data[field.id];
      if (!value) continue;

      if (field.type === 'EMAIL' || field.field_key.toLowerCase() === 'email') {
        email = value;
      }
      if (
        field.field_key.toLowerCase() === 'name' ||
        field.field_key.toLowerCase() === 'full_name' ||
        field.label.toLowerCase().includes('name')
      ) {
        fullName = value;
      }
    }

    let leadId: string | null = null;
    const shouldCreateLead = form.lead_type_default !== 'NONE';

    if (shouldCreateLead) {
      if (email) {
        const { data: existingLead } = await supabase
          .from('leads')
          .select('id')
          .eq('user_id', form.user_id)
          .eq('email', email)
          .maybeSingle();

        if (existingLead) {
          leadId = existingLead.id;

          await supabase
            .from('leads')
            .update({ last_activity_at: new Date().toISOString() })
            .eq('id', leadId);
        }
      }

      if (!leadId) {
        const leadStatus = form.default_status || 'NEW';
        const leadData = {
          user_id: form.user_id,
          full_name: fullName || 'Unbekannt',
          email: email,
          lead_type: form.lead_type_default as 'CUSTOMER' | 'PARTNER',
          status: leadStatus,
          source: form.default_source || `Formular: ${form.name}`,
          last_activity_at: new Date().toISOString(),
        };

        const { data: newLead, error: leadError } = await supabase
          .from('leads')
          .insert(leadData)
          .select()
          .single();

        if (leadError) throw leadError;
        leadId = newLead.id;

        // Create todo for new leads with status 'NEW'
        if (leadStatus === 'NEW') {
          try {
            await createTodoForNewLead({
              userId: form.user_id,
              leadId: newLead.id,
              leadName: newLead.full_name,
              source: 'form',
              formName: form.name,
            });
          } catch (todoError) {
            logger.error('Error creating todo for new lead from form:', todoError);
          }
        }
      }
    }

    const { data: submission, error: submissionError } = await supabase
      .from('form_submissions')
      .insert({
        form_id: form.id,
        user_id: form.user_id,
        lead_id: leadId,
      })
      .select()
      .single();

    if (submissionError) throw submissionError;

    const answers = form.fields.map((field) => ({
      submission_id: submission.id,
      form_field_id: field.id,
      value: data[field.id] || '',
    }));

    const { error: answersError } = await supabase
      .from('form_answers')
      .insert(answers);

    if (answersError) throw answersError;

    if (leadId) {
      const activityContent = leadId
        ? `Formular "${form.name}" eingereicht`
        : `Formular "${form.name}" eingereicht und Lead erstellt`;

      await supabase.from('lead_activities').insert({
        user_id: form.user_id,
        lead_id: leadId,
        type: 'FORM_SUBMISSION',
        content: activityContent,
      });
    }

    return { success: true };
  } catch (error) {
    logger.error('Form submission error:', error);
    return { success: false, error: 'Submission failed' };
  }
}

export async function getFormSubmissions(
  formId: string,
  userId: string
): Promise<FormSubmissionWithAnswers[]> {
  const { data: submissions, error } = await supabase
    .from('form_submissions')
    .select('*')
    .eq('form_id', formId)
    .eq('user_id', userId)
    .order('submitted_at', { ascending: false });

  if (error) throw error;
  if (!submissions) return [];

  const submissionIds = submissions.map((s) => s.id);
  const { data: answers } = await supabase
    .from('form_answers')
    .select('*, form_fields(*)')
    .in('submission_id', submissionIds);

  const answersBySubmission: Record<string, (FormAnswer & { field?: FormField })[]> = {};
  answers?.forEach((answer) => {
    if (!answersBySubmission[answer.submission_id]) {
      answersBySubmission[answer.submission_id] = [];
    }
    answersBySubmission[answer.submission_id].push({
      ...answer,
      field: answer.form_fields as FormField | undefined,
    });
  });

  return submissions.map((submission) => ({
    ...submission,
    answers: answersBySubmission[submission.id] || [],
  }));
}

export async function getSubmissionsForLead(
  leadId: string,
  userId: string
): Promise<FormSubmissionWithAnswers[]> {
  const { data: submissions, error } = await supabase
    .from('form_submissions')
    .select('*, forms(*)')
    .eq('lead_id', leadId)
    .eq('user_id', userId)
    .order('submitted_at', { ascending: false });

  if (error) throw error;
  if (!submissions) return [];

  const submissionIds = submissions.map((s) => s.id);

  if (submissionIds.length === 0) return [];

  const { data: answers } = await supabase
    .from('form_answers')
    .select('*, form_fields(*)')
    .in('submission_id', submissionIds);

  const answersBySubmission: Record<string, (FormAnswer & { field?: FormField })[]> = {};
  answers?.forEach((answer) => {
    if (!answersBySubmission[answer.submission_id]) {
      answersBySubmission[answer.submission_id] = [];
    }
    answersBySubmission[answer.submission_id].push({
      ...answer,
      field: answer.form_fields as FormField | undefined,
    });
  });

  return submissions.map((submission) => ({
    ...submission,
    form: submission.forms as Form | undefined,
    answers: answersBySubmission[submission.id] || [],
  }));
}

export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[äÄ]/g, 'ae')
    .replace(/[öÖ]/g, 'oe')
    .replace(/[üÜ]/g, 'ue')
    .replace(/[ß]/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function generateFieldKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[äÄ]/g, 'ae')
    .replace(/[öÖ]/g, 'oe')
    .replace(/[üÜ]/g, 'ue')
    .replace(/[ß]/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

// Default contact form configuration
const DEFAULT_CONTACT_FORM = {
  name: 'Kontaktformular',
  description: 'Standard-Kontaktformular für Anfragen',
  slug: 'kontakt',
  is_published: true,
  is_system: true,
  lead_type_default: 'CUSTOMER' as LeadTypeDefault,
  default_status: 'NEW',
  default_source: 'Kontaktformular',
};

const DEFAULT_CONTACT_FORM_FIELDS = [
  {
    type: 'TEXT' as const,
    label: 'Name',
    placeholder: 'Vor- und Nachname',
    required: true,
    field_key: 'name',
    field_order: 0,
  },
  {
    type: 'EMAIL' as const,
    label: 'E-Mail',
    placeholder: 'name@beispiel.de',
    required: true,
    field_key: 'email',
    field_order: 1,
  },
  {
    type: 'PHONE' as const,
    label: 'Telefon',
    placeholder: '+49 123 456789',
    required: false,
    field_key: 'phone',
    field_order: 2,
  },
  {
    type: 'TEXTAREA' as const,
    label: 'Nachricht',
    placeholder: 'Ihre Nachricht...',
    required: true,
    field_key: 'message',
    field_order: 3,
  },
];

/**
 * Ensures the user has a default contact form.
 * Creates one if it doesn't exist yet.
 */
export async function ensureDefaultContactForm(userId: string): Promise<Form> {
  // Check if user already has a system form
  const { data: existingForm } = await supabase
    .from('forms')
    .select('*')
    .eq('user_id', userId)
    .eq('is_system', true)
    .maybeSingle();

  if (existingForm) {
    return existingForm;
  }

  // Check if slug 'kontakt' already exists for this user
  let slug = DEFAULT_CONTACT_FORM.slug;
  const { data: slugCheck } = await supabase
    .from('forms')
    .select('id')
    .eq('user_id', userId)
    .eq('slug', slug)
    .maybeSingle();

  if (slugCheck) {
    slug = `kontakt-${Date.now()}`;
  }

  // Create the default contact form
  const { data: form, error: formError } = await supabase
    .from('forms')
    .insert({
      ...DEFAULT_CONTACT_FORM,
      slug,
      user_id: userId,
    })
    .select()
    .single();

  if (formError) throw formError;

  // Create the default fields
  const fieldsToInsert = DEFAULT_CONTACT_FORM_FIELDS.map((field) => ({
    ...field,
    form_id: form.id,
    user_id: userId,
  }));

  const { error: fieldsError } = await supabase
    .from('form_fields')
    .insert(fieldsToInsert);

  if (fieldsError) throw fieldsError;

  return form;
}
